<template>
  <video :id="`player-${id}`" class="cld-video-player" />
</template>

<script setup lang="ts">
import type { VideoTrack } from '#types/media'

const {
  autoplay = true,
  cloudinaryAsset,
  height,
  hideControls = true,
  id,
  loop = true,
  muted = true,
  seoDescription,
  seoTitle,
  width,
  tracks = [],
} = defineProps<{
  autoplay?: boolean
  cloudinaryAsset?: {
    cloudName: string
    publicId: string
  }
  height?: number
  hideControls?: boolean
  id?: string
  loop?: boolean
  muted?: boolean
  seoDescription?: string
  seoTitle?: string
  url?: string
  width?: number
  tracks?: VideoTrack[]
}>()

const locale = useLocale()

const findDefaultTrack = (tracks) => {
  const bestMatch = tracks.find(({ language }) => language === locale)

  if (bestMatch) {
    return bestMatch
  }

  return tracks.find(({ language }) => locale.includes(language)) || tracks[0]
}

onMounted(async () => {
  import('cloudinary-video-player/cld-video-player.min.css')
  const cloudinary = await import('cloudinary-video-player')
  // @ts-expect-error: No types for cloudinary-video-player/chapters
  await import('cloudinary-video-player/chapters')

  const tracksMap = tracks.map(({ src, label, srclang, kind }) => ({
    kind,
    url: src,
    label,
    language: srclang,
  }))

  const transcripts = tracksMap.filter((t) => t.kind === 'transcripts')
  const defaultTrack = findDefaultTrack(transcripts)
  const captions = transcripts.map((t) => ({
    ...t,
    default: t === defaultTrack,
  }))
  const chapters = tracksMap.find((t) => t.kind === 'chapters')

  const player = cloudinary.videoPlayer(`player-${id}`, {
    autoplay,
    autoplayMode: 'on-scroll',
    chaptersButton: true,
    cloudName: cloudinaryAsset!.cloudName,
    controls: !hideControls,
    fluid: true,
    height,
    hideContextMenu: true,
    loop,
    muted,
    showLogo: false,
    width,
  })

  player.source(cloudinaryAsset!.publicId, {
    info: {
      title: seoTitle,
      subtitle: seoDescription,
    },
    textTracks: {
      captions,
    },
    chapters
  })
})
</script>
